import type { PaAPI } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'

export type Exclusion = PaAPI.Get['/exclusion/{id}'] & {
  // TODO: these fields are only available when requesting an exclusion with include=true.
  // This changes the exclusion type to `ExclusionVersionWithRelations` (vs `ExclusionWithRelations`)
  // which doesn't match the base schema and isn't available in paapi types
  id: string
  is_all_connectors?: boolean
  condition_codes?: {
    condition_code: string
  }[]
  pricing_methodology_codes?: {
    pricing_methodology_code: string
  }[]
  source_types?: {
    source_type_level_1: {
      id: string
      name: string
    }
  }[]
  revenue_codes?: {
    revenue_code: string
  }[]
  type_of_bill_codes?: {
    type_of_bill_code: string
  }[]
  ndc_codes?: { code: string }[]
}

export interface ExclusionImpact {
  exclusion_id: string
  impacted_claim_count: number
  impacted_claim_count_active?: number
  impacted_claims_observation?: number
  max_savings_opportunity: number
  max_savings_opportunity_active?: number
  max_savings_opportunity_observation?: number
  min_savings_opportunity: number
  min_savings_opportunity_active?: number
  min_savings_opportunity_observation?: number
  name: string
}

export const useActiveExclusion = defineStore('active-exclusion', () => {
  const activeExclusion = ref<Exclusion | null>(null)
  const duplicateSourceId = ref<string | null>(null)

  const hasActiveExclusion = computed(() =>
    Boolean(activeExclusion.value && Object.keys(activeExclusion.value).length),
  )

  const clearActiveExclusionEdit = () => {
    activeExclusion.value = null
    duplicateSourceId.value = null
  }

  const setActiveExclusionEdit = (exclusion: Exclusion | null) => {
    activeExclusion.value = exclusion
    duplicateSourceId.value = null
  }

  const setActiveExclusionDuplicate = (exclusion: Exclusion | null) => {
    activeExclusion.value = exclusion
    duplicateSourceId.value = exclusion?.id ?? null
  }

  const isInReview = ref(false)

  /** Event Hooks */
  const saveDraft = createEventHook<Exclusion>()
  const reviewExclusion = createEventHook<Exclusion>()

  return {
    activeExclusion,
    duplicateSourceId,
    isInReview,
    hasActiveExclusion,
    setActiveExclusionEdit,
    setActiveExclusionDuplicate,
    clearActiveExclusionEdit,
    onSaveDraft: saveDraft.on,
    onReviewExclusion: reviewExclusion.on,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useActiveExclusion, import.meta.hot))
